// src/components/tpv/TpvConfiguration.jsx

import React, { useState, useEffect } from 'react';
import { get, post, del } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faKey,
    faCopy,
    faEye,
    faEyeSlash,
    faQrcode,
    faCog,
    faCheck,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { tpvConfigurations, tpvStats, tpvUtils } from '../../helpers/tpvHelper';

const TpvConfiguration = ({ tiendaId, marcaId }) => {
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [showApiKeys, setShowApiKeys] = useState({});
    const [stats, setStats] = useState(null);

    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        tipoSoftware: 'agora',
        ipAddress: ''
    });

    useEffect(() => {
        if (tiendaId) {
            fetchConfigurations();
            fetchStats();
        }
    }, [tiendaId]);

    const fetchConfigurations = async () => {
        try {
            setLoading(true);
            const response = await tpvConfigurations.getByTienda(tiendaId);
            setConfigurations(response);
        } catch (error) {
            console.error('Error fetching TPV configurations:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudieron cargar las configuraciones TPV',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await tpvStats.getByTienda(tiendaId);
            setStats(response);
        } catch (error) {
            console.error('Error fetching TPV stats:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar configuración
        const validation = tpvUtils.validateConfiguration(formData);
        if (!validation.valid) {
            Swal.fire({
                title: 'Error de validación',
                text: validation.errors.join(', '),
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Preparar datos para enviar: omitir ipAddress si está vacío
        const dataToSend = { ...formData };
        if (!dataToSend.ipAddress || dataToSend.ipAddress.trim() === '') {
            delete dataToSend.ipAddress;
        }

        try {
            if (editingConfig) {
                // Actualizar configuración existente
                await tpvConfigurations.update(editingConfig._id, dataToSend);
                Swal.fire({
                    title: 'Éxito',
                    text: 'Configuración TPV actualizada correctamente',
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                });
            } else {
                // Crear nueva configuración
                const response = await tpvConfigurations.create(tiendaId, dataToSend);

                // Debug: mostrar la respuesta completa
                console.log('TPV Configuration response:', response);

                // Mostrar API Key si está disponible
                if (response && response.apiKey) {
                    Swal.fire({
                        title: 'Configuración TPV Creada',
                        html: `
                            <div class="text-left">
                                <p class="mb-3">La configuración TPV se ha creado exitosamente.</p>
                                <div class="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                    <p class="text-sm font-medium text-yellow-800 mb-2">🔑 API Key generada:</p>
                                    <div class="bg-white p-2 rounded border">
                                        <code class="text-sm font-mono text-gray-800 break-all">${response.apiKey}</code>
                                    </div>
                                    <p class="text-xs text-yellow-700 mt-2">
                                        Guarda esta API Key de forma segura. La necesitarás para configurar Agora Software.
                                    </p>
                                </div>
                            </div>
                        `,
                        icon: 'success',
                        confirmButtonText: 'Copiar API Key',
                        preConfirm: () => {
                            navigator.clipboard.writeText(response.apiKey);
                        }
                    });
                } else {
                    // Debug: mostrar qué se recibió
                    console.log('No API Key found in response:', response);
                    Swal.fire({
                        title: 'Configuración Creada',
                        html: `
                            <div class="text-left">
                                <p class="mb-3">La configuración TPV se ha creado exitosamente.</p>
                                <div class="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                    <p class="text-sm font-medium text-blue-800 mb-2">ℹ️ Información:</p>
                                    <p class="text-xs text-blue-700">
                                        La API Key se generará automáticamente. Puedes verla en la pestaña "Información" después de recargar la página.
                                    </p>
                                </div>
                            </div>
                        `,
                        icon: 'success',
                        confirmButtonText: 'Aceptar'
                    });
                }
            }

            setShowModal(false);
            setEditingConfig(null);
            setFormData({
                nombre: '',
                descripcion: '',
                tipoSoftware: 'agora',
                ipAddress: ''
            });
            fetchConfigurations();
            fetchStats();
        } catch (error) {
            console.error('Error saving TPV configuration:', error);
            console.error('Error response:', error.response || error);

            // Mostrar más detalles del error para debugging
            const errorMessage = error.response?.data?.message || error.message || 'Error al guardar la configuración TPV';

            Swal.fire({
                title: 'Error',
                html: `
                    <div class="text-left">
                        <p class="mb-2">${errorMessage}</p>
                        ${error.response?.data?.details ? `
                            <div class="bg-red-50 p-2 rounded border border-red-200 mt-2">
                                <p class="text-xs text-red-700">Detalles: ${error.response.data.details}</p>
                            </div>
                        ` : ''}
                    </div>
                `,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    const handleEdit = (config) => {
        setEditingConfig(config);
        setFormData({
            nombre: config.nombre,
            descripcion: config.descripcion || '',
            tipoSoftware: config.tipoSoftware || 'agora',
            ipAddress: config.ipAddress || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (configId) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await tpvConfigurations.delete(configId);
                Swal.fire({
                    title: 'Eliminado',
                    text: 'Configuración TPV eliminada correctamente',
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                });
                fetchConfigurations();
                fetchStats();
            } catch (error) {
                console.error('Error deleting TPV configuration:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Error al eliminar la configuración TPV',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    };

    const handleRegenerateApiKey = async (configId) => {
        const result = await Swal.fire({
            title: 'Regenerar API Key',
            text: '¿Estás seguro de que quieres regenerar la API Key? La anterior dejará de funcionar.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, regenerar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const response = await tpvConfigurations.regenerateApiKey(configId);
                Swal.fire({
                    title: 'API Key Regenerada',
                    html: `
            <div class="text-left">
              <p class="mb-2">Nueva API Key:</p>
              <div class="bg-gray-100 p-3 rounded font-mono text-sm break-all">
                ${response.newApiKey}
              </div>
              <p class="text-sm text-gray-600 mt-2">
                Guarda esta clave de forma segura. No se volverá a mostrar.
              </p>
            </div>
          `,
                    icon: 'success',
                    confirmButtonText: 'Copiar y Cerrar',
                    preConfirm: () => {
                        navigator.clipboard.writeText(response.newApiKey);
                    }
                });
                fetchConfigurations();
            } catch (error) {
                console.error('Error regenerating API key:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Error al regenerar la API Key',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Swal.fire({
            title: 'Copiado',
            text: 'API Key copiada al portapapeles',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const toggleApiKeyVisibility = (configId) => {
        setShowApiKeys(prev => ({
            ...prev,
            [configId]: !prev[configId]
        }));
    };

    const maskApiKey = (apiKey) => {
        return tpvUtils.maskApiKey(apiKey);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Cargando configuraciones TPV...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Configuración TPV</h2>
                    <p className="text-gray-600">Gestiona las configuraciones de terminales de punto de venta</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    Nueva Configuración
                </button>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow border">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <FontAwesomeIcon icon={faCog} className="text-blue-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-gray-600">Total Configuraciones</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.totalConfigurations}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow border">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-gray-600">Activas</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.activeConfigurations}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow border">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <FontAwesomeIcon icon={faQrcode} className="text-purple-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-gray-600">Total Requests</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.totalRequests}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow border">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <FontAwesomeIcon icon={faTimes} className="text-orange-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-gray-600">Último Uso</p>
                                <p className="text-sm font-medium text-gray-800">
                                    {stats.lastUsed ? new Date(stats.lastUsed).toLocaleDateString() : 'Nunca'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Configurations List */}
            <div className="bg-white rounded-lg shadow border">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuraciones TPV</h3>

                    {configurations.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FontAwesomeIcon icon={faCog} className="text-gray-400 text-2xl" />
                            </div>
                            <h4 className="text-lg font-medium text-gray-800 mb-2">No hay configuraciones TPV</h4>
                            <p className="text-gray-600 mb-4">Crea tu primera configuración TPV para comenzar a usar el sistema de QR</p>
                            <button
                                onClick={() => setShowModal(true)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Crear Configuración
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {configurations.map((config) => (
                                <div key={config._id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="text-lg font-semibold text-gray-800">{config.nombre}</h4>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.activo
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {config.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>

                                            {config.descripcion && (
                                                <p className="text-gray-600 text-sm mb-2">{config.descripcion}</p>
                                            )}

                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span><strong>Software:</strong> {config.tipoSoftware}</span>
                                                {config.ipAddress && <span><strong>IP:</strong> {config.ipAddress}</span>}
                                                <span><strong>Requests:</strong> {config.usageCount}</span>
                                                <span><strong>Creado:</strong> {new Date(config.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {/* API Key */}
                                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                                                <FontAwesomeIcon icon={faKey} className="text-gray-500" />
                                                <span className="font-mono text-sm">
                                                    {showApiKeys[config._id] ? config.apiKey : maskApiKey(config.apiKey)}
                                                </span>
                                                <button
                                                    onClick={() => toggleApiKeyVisibility(config._id)}
                                                    className="text-gray-500 hover:text-gray-700"
                                                >
                                                    <FontAwesomeIcon icon={showApiKeys[config._id] ? faEyeSlash : faEye} />
                                                </button>
                                                <button
                                                    onClick={() => copyToClipboard(config.apiKey)}
                                                    className="text-gray-500 hover:text-gray-700"
                                                >
                                                    <FontAwesomeIcon icon={faCopy} />
                                                </button>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleRegenerateApiKey(config._id)}
                                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                                    title="Regenerar API Key"
                                                >
                                                    <FontAwesomeIcon icon={faKey} />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(config)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Editar"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(config._id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-white">
                                {editingConfig ? 'Editar Configuración TPV' : 'Nueva Configuración TPV'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setEditingConfig(null);
                                    setFormData({
                                        nombre: '',
                                        descripcion: '',
                                        tipoSoftware: 'agora',
                                        ipAddress: ''
                                    });
                                }}
                                className="text-white hover:text-gray-200 transition-colors"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ej: TPV Principal - Caja 1"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Descripción opcional del terminal"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo de Software *
                                </label>
                                <select
                                    value={formData.tipoSoftware}
                                    onChange={(e) => setFormData({ ...formData, tipoSoftware: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                >
                                    <option value="agora">Agora Software</option>
                                    <option value="otro">Otro</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dirección IP (Opcional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.ipAddress}
                                    onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="192.168.1.100"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingConfig(null);
                                        setFormData({
                                            nombre: '',
                                            descripcion: '',
                                            tipoSoftware: 'agora',
                                            ipAddress: ''
                                        });
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {editingConfig ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TpvConfiguration;
